import { useState } from "react";
import type { ClassifyDetail } from "../api/client";
import styles from "./ClassifyDetailPanel.module.css";

/**
 * Split-document mode for the classify detail panel (5d-4a).
 * Extracted from ClassifyDetailPanel.tsx (R-260 CS-station cleanup).
 *
 * Owns its own boundary selection — mounting on split-mode entry and
 * unmounting on exit naturally resets the boundaries each time. The shared
 * busy/actionError slot lives in the parent (via useParkedDocAction) and is
 * passed in; the parent clears the error on cancel.
 */
export function SplitPanel({
  detail,
  busy,
  actionError,
  onApplySplit,
  onCancel,
}: {
  detail: ClassifyDetail;
  busy: boolean;
  actionError: string | null;
  onApplySplit: (boundaryIds: string[]) => void;
  onCancel: () => void;
}) {
  const [boundaries, setBoundaries] = useState<Set<string>>(new Set());

  function toggleBoundary(pageId: string) {
    setBoundaries((cur) => {
      const next = new Set(cur);
      if (next.has(pageId)) next.delete(pageId);
      else next.add(pageId);
      return next;
    });
  }

  // A new group starts at page 1 and at each marked boundary.
  const splitPages = [...detail.pages].sort((a, b) => a.pageNumber - b.pageNumber);
  const splitGroups: { start: number; end: number }[] = [];
  if (splitPages.length > 0) {
    let groupStart = splitPages[0]!.pageNumber;
    for (let i = 1; i < splitPages.length; i++) {
      const page = splitPages[i]!;
      if (boundaries.has(page.id)) {
        splitGroups.push({ start: groupStart, end: splitPages[i - 1]!.pageNumber });
        groupStart = page.pageNumber;
      }
    }
    splitGroups.push({ start: groupStart, end: splitPages[splitPages.length - 1]!.pageNumber });
  }

  const splitPreviewText =
    splitGroups.length > 1
      ? `Will create ${splitGroups.length} documents: ${splitGroups
          .map((g) => (g.start === g.end ? `page ${g.start}` : `pages ${g.start}–${g.end}`))
          .join(", ")}`
      : `Mark boundaries to split into multiple documents`;

  function handleApply() {
    if (boundaries.size === 0) return;
    // splitPages is already sorted ascending by pageNumber, so filtering it
    // yields the boundary ids in page order — no extra Map or sort needed.
    const sortedBoundaryIds = splitPages.filter((p) => boundaries.has(p.id)).map((p) => p.id);
    onApplySplit(sortedBoundaryIds);
  }

  return (
    <div className={styles.splitPanel}>
      <div className={styles.splitList}>
        {splitPages.map((page) => (
          <div key={page.id} className={styles.splitRow}>
            {/* F-059 — show the page image so boundaries are chosen against
                visible content. Split mode previously rendered text-only rows,
                hiding the document entirely ("impossible to actually split"). */}
            {page.thumbnailPath ? (
              <img
                src={page.thumbnailPath}
                alt={`Page ${page.pageNumber}`}
                className={styles.splitThumb}
              />
            ) : (
              <div className={styles.splitThumbPlaceholder} aria-hidden="true">
                {page.pageNumber}
              </div>
            )}
            {page.pageNumber === 1 ? (
              <span className={styles.splitImplicitStart}>
                Document 1 starts here — Page {page.pageNumber}
              </span>
            ) : (
              <label className={styles.splitLabel}>
                <input
                  type="checkbox"
                  aria-label={`Start a new document at page ${page.pageNumber}`}
                  checked={boundaries.has(page.id)}
                  disabled={detail.duplex && page.pageNumber % 2 === 0}
                  onChange={() => toggleBoundary(page.id)}
                />
                <span>Start a new document at page {page.pageNumber}</span>
              </label>
            )}
          </div>
        ))}
      </div>
      <div className={styles.splitPreview}>{splitPreviewText}</div>
      <div className={styles.splitActions}>
        <button
          type="button"
          className={styles.btn}
          disabled={boundaries.size === 0 || busy}
          onClick={handleApply}
        >
          Apply split
        </button>
        <button type="button" className={styles.btn} onClick={onCancel}>
          Cancel
        </button>
        {actionError && <p className={styles.actionError}>{actionError}</p>}
      </div>
    </div>
  );
}
